import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView, getVisitorId } from '@/lib/analytics';

/**
 * Hook to track page views on route changes.
 *
 * - Skips first render (worker's injected script handles initial page load)
 * - Deduplicates same-path navigations
 * - Captures UTM parameters
 *
 * Should be used once at the app root level inside BrowserRouter.
 */
export function usePageTracking(): void {
  const location = useLocation();
  const lastTrackedPath = useRef<string | null>(null);

  useEffect(() => {
    // Skip first render - the injected script handles initial page view
    if (lastTrackedPath.current === null) {
      lastTrackedPath.current = location.pathname;
      return;
    }

    // Skip if same path
    if (location.pathname === lastTrackedPath.current) {
      return;
    }

    lastTrackedPath.current = location.pathname;

    // Need visitor ID to track
    const visitorId = getVisitorId();
    if (!visitorId) {
      return;
    }

    // Read UTM params
    const params = new URLSearchParams(location.search);

    // Small delay to ensure title is updated
    setTimeout(() => {
      trackPageView({
        page_path: location.pathname,
        page_url: window.location.href,
        referrer: document.referrer || undefined,
        title: document.title || undefined,
        utm_source: params.get('utm_source') || undefined,
        utm_medium: params.get('utm_medium') || undefined,
        utm_campaign: params.get('utm_campaign') || undefined,
      });
    }, 100);
  }, [location.pathname, location.search]);
}
